import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactSearchAutocomplete } from "react-search-autocomplete";

// Chakra imports
import {
  Flex,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  Td,
  Badge,
  Button,
  Input,
  useColorModeValue,
  SimpleGrid,
  Spacer,
  InputGroup,
  IconButton,
  InputLeftElement,
  InputRightElement,
  Box,
  Heading,
  HStack,
  VStack,
  Divider,
  Container,
  Stack,
  Link,
  Icon,
} from "@chakra-ui/react";

import { NavLink } from "react-router-dom";
import {
  AddIcon,
  MinusIcon,
  EditIcon,
  CheckIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { ExternalLinkIcon } from "@chakra-ui/icons";

// Custom components
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import axios from "axios";
import { API_SERVER } from "config/constant";

function OrderDetails() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgStatus = useColorModeValue("gray.400", "#1a202c");
  const colorStatus = useColorModeValue("white", "gray.400");
  const brandTeal = useColorModeValue("brand.500", "teal.600");
  const searchIconColor = useColorModeValue("gray.700", "gray.200");
  const inputBg = useColorModeValue("white", "gray.800");

  const [order, setOrder] = useState();
  const [products, setProducts] = useState([]);
  const productsRef = useRef([]);
  const [picking, setPicking] = useState([]);
  const [allPicking, setAllPicking] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showScanner, setShowScanner] = useState(1);
  const [value, setValue] = useState(0);
  const [manualCode, setManualCode] = useState("");
  const [orderCodeValidation, setOrderCodeValidation] = useState(0);
  const [data, setData] = useState("Scanner activo - Espera próxima lectura");
  const [count, setCount] = useState(0);
  const [lastScannedCode, setLastScannedCode] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [lastSuccessfulScan, setLastSuccessfulScan] = useState(null);
  const [scanAttempts, setScanAttempts] = useState({});
  const [globalLastRequest, setGlobalLastRequest] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const min = 1;
  const max = 100;

  const { reference } = useParams();
  const buttonRef = useRef(true);
  const quantityRef = useRef(true);

  useEffect(() => {
    getOrderDetails();
  }, []);

  useEffect(() => {
    if (products && products.length > 0 && !allPicking.length) {
      getAllPicking();
    }
  }, [products]);

  useEffect(() => {
    if (selectedProduct) {
      updatePickingTable();
      if (!isScanning && value === 0 && count === 0) {
        setCount(0);
        setValue(0);
      }
      setManualCode("");
    }
  }, [selectedProduct]);

  // Efecto para mantener el input del scanner enfocado
  useEffect(() => {
    const focusScanner = () => {
      if (quantityRef.current) {
        quantityRef.current.focus();
      }
    };

    // Enfocar al montar
    focusScanner();

    // Enfocar periódicamente para asegurarse que no pierda el foco
    const interval = setInterval(focusScanner, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Efecto para enfocar después de cada lectura
  useEffect(() => {
    if (!isScanning && quantityRef.current) {
      setTimeout(() => {
        quantityRef.current.focus();
      }, 100);
    }
  }, [isScanning, manualCode]);

  // Función para normalizar códigos (definida globalmente)
  const normalizeCode = (code) => {
    if (!code) return "";
    return code
      .toString()
      .trim()
      .replace(/[\s\-_]/g, "")
      .replace(/[\r\n]/g, "")
      .toUpperCase();
  };

  // Función centralizada para buscar productos
  const findProductByCode = (code) => {
    const normalizedCode = normalizeCode(code);
    return productsRef.current.find((p) => {
      const fields = [
        p.code,
        p.upc,
        p.mpn,
        p.ean,
        p.barcode,
        p.sku,
        p.reference,
      ];
      return fields.some((field) => normalizeCode(field) === normalizedCode);
    });
  };

  // Función centralizada para validaciones de throttling y duplicados
  const canProcessProduct = (product, currentTime) => {
    // Protección contra throttling
    const timeSinceLastRequest = globalLastRequest
      ? currentTime - globalLastRequest
      : 999999;

    if (timeSinceLastRequest < 2000) {
      return {
        canProcess: false,
        message: `Proteccion contra throttling: esperando ${2000 - timeSinceLastRequest
          }ms`,
        delay: 1000,
      };
    }

    // Prevenir duplicados
    const timeSinceLastScan = lastSuccessfulScan
      ? currentTime - lastSuccessfulScan.time
      : 999999;

    if (
      lastSuccessfulScan &&
      lastSuccessfulScan.productCode === product.code &&
      timeSinceLastScan < 4000
    ) {
      return {
        canProcess: false,
        message: `Escaneo duplicado prevenido para ${product.code} (${timeSinceLastScan}ms)`,
        delay: 1000,
      };
    }

    // Contar intentos
    const currentAttempts = scanAttempts[product.code] || 0;
    if (currentAttempts >= 3 && timeSinceLastScan < 10000) {
      return {
        canProcess: false,
        message: `Demasiados intentos para ${product.code}`,
        delay: 1000,
      };
    }

    return { canProcess: true };
  };

  // Función para limpiar y resetear estado
  const resetScannerState = (delay = 0) => {
    setTimeout(() => {
      setManualCode("");
      setData("Scanner activo - Espera próxima lectura");
      // Re-enfocar el input oculto
      if (quantityRef.current) {
        quantityRef.current.focus();
      }
    }, delay);
  };

  // Función principal para procesar cualquier código (scanner o manual)
  const processCode = async (code, source = "scanner") => {
    if (!code || !code.trim()) return;

    const trimmedCode = code.trim();
    const currentTime = Date.now();

    console.log(`Código recibido por ${source}:`, trimmedCode);
    setData(`Procesando: ${trimmedCode}`);

    // Buscar producto
    const foundProduct = findProductByCode(trimmedCode);

    if (!foundProduct) {
      console.log("Producto NO encontrado:", trimmedCode);
      setData(`Producto no encontrado: ${trimmedCode}`);
      resetScannerState(1000);
      return;
    }

    if (foundProduct.quantity <= 0) {
      console.log("Sin stock disponible para:", foundProduct.code);
      setData(`Sin stock: ${foundProduct.code}`);
      resetScannerState(1000);
      return;
    }

    // Validar throttling y duplicados
    const validation = canProcessProduct(foundProduct, currentTime);
    if (!validation.canProcess) {
      console.log(validation.message);
      setData(validation.message);
      resetScannerState(validation.delay);
      return;
    }

    console.log("Producto encontrado:", foundProduct.code);
    setData(`Procesando: ${foundProduct.code}`);

    // Marcar tiempo de esta peticion global
    setGlobalLastRequest(currentTime);

    // Incrementar contador de intentos
    setScanAttempts((prev) => ({
      ...prev,
      [foundProduct.code]: (prev[foundProduct.code] || 0) + 1,
    }));

    // Hacer picking automatico de 1 unidad
    await performAutoPicking(foundProduct);

    // Limpiar y reenfocar
    resetScannerState(500);
  };

  const getOrderDetails = async () => {
    await axios.get(`${API_SERVER}orders/` + reference).then(({ data }) => {
      console.log(data);
      setOrder(data.order);
      setProducts(data.products);
      productsRef.current = data.products;
    });
  };

  const getAllPicking = async () => {
    try {
      await axios.get(`${API_SERVER}orders/` + reference).then(({ data }) => {
        if (data.picking) {
          setAllPicking(data.picking);
        } else if (data.products) {
          const allPickingPromises = data.products.map((product) =>
            axios
              .get(
                `${API_SERVER}picking/picking_info/` +
                reference +
                `/` +
                product.code
              )
              .then((response) => response.data)
              .catch(() => [])
          );

          Promise.all(allPickingPromises).then((results) => {
            const flatPicking = results.flat().filter((item) => item);
            setAllPicking(flatPicking);
          });
        } else {
          setAllPicking([]);
        }
      });
    } catch (error) {
      console.log("Error obteniendo picking:", error);
      if (products && products.length > 0) {
        const allPickingPromises = products.map((product) =>
          axios
            .get(
              `${API_SERVER}picking/picking_info/` +
              reference +
              `/` +
              product.code
            )
            .then((response) => response.data)
            .catch(() => [])
        );

        Promise.all(allPickingPromises).then((results) => {
          const flatPicking = results.flat().filter((item) => item);
          setAllPicking(flatPicking);
        });
      } else {
        setAllPicking([]);
      }
    }
  };

  const updatePickingTable = async () => {
    if (!selectedProduct) return;

    await axios
      .get(
        `${API_SERVER}picking/picking_info/` +
        reference +
        `/` +
        selectedProduct.code
      )
      .then(({ data }) => {
        console.log(data);
        setPicking(data);
      });
  };

  const handleChange = (event) => {
    const value = Math.max(min, Math.min(max, Number(event.target.value)));
    setValue(value);
  };

  const validate_code = (event) => {
    const codeValue = event.target.value;
    setManualCode(codeValue);

    console.log("Input recibido:", JSON.stringify(codeValue));

    // Los scanners USB generalmente terminan con Enter (caracter 13)
    if (
      codeValue.includes("\n") ||
      codeValue.includes("\r") ||
      event.keyCode === 13
    ) {
      const cleanCode = codeValue.replace(/[\r\n]/g, "").trim();
      console.log("Código detectado:", cleanCode);
      if (cleanCode && cleanCode.length > 0) {
        processCode(cleanCode, "scanner");
        // Limpiar el input inmediatamente
        setManualCode("");
      }
    }
  };

  const performAutoPicking = async (product) => {
    try {
      console.log(
        `Realizando picking automatico para ${product.code} - 1 unidad`
      );

      // Esperar un poco antes de hacer la peticion para evitar throttling
      await new Promise((resolve) => setTimeout(resolve, 300));

      const formData = new FormData();
      formData.append("order_id", reference);
      formData.append("product_id", product.id);
      formData.append("product_code", product.code);
      formData.append("upc", product.upc || product.code);
      formData.append("quantity", 1);
      formData.append("total", product.quantity);

      const response = await axios.post(
        `${API_SERVER}picking/save_picking`,
        formData
      );

      if (response.data) {
        console.log("Picking guardado automaticamente:", response.data);

        // Registrar escaneo exitoso y limpiar intentos
        setLastSuccessfulScan({
          productCode: product.code,
          time: Date.now(),
        });

        // Resetear contador de intentos para este producto
        setScanAttempts((prev) => ({
          ...prev,
          [product.code]: 0,
        }));

        await getOrderDetails();
        await getAllPicking(); // Actualizar historial

        console.log(`Picking completado para ${product.code}`);

        setLastScannedCode("");
      }
    } catch (error) {
      console.error("Error en picking automatico:", error);
      setData(`Error: ${error.response?.data?.message || "Error en picking"}`);

      // Si es error de throttling, esperar mas tiempo
      if (error.response?.status === 429) {
        console.log("Error de throttling, esperando 5 segundos...");
        setData(`Throttling detectado, esperando...`);
        setTimeout(() => {
          setData("Scanner activo - Espera próxima lectura");
        }, 5000);
      }
    }
  };

  const incrementQuantity = () => {
    if (count < selectedProduct.quantity) {
      const newCount = count + 1;
      setCount(newCount);
      console.log("Incrementado: " + newCount);
      setValue(newCount);
      quantityRef.current.disabled = false;
    } else {
      quantityRef.current.disabled = true;
    }

    if (value > 0 && selectedProduct && selectedProduct.quantity > 0) {
      if (orderCodeValidation) {
        buttonRef.current.disabled = false;
      }
    }
  };

  const decreaseQuantity = () => {
    if (count > 0) {
      const newCount = count - 1;
      setCount(newCount);
      console.log("Decrementado: " + newCount);
      setValue(newCount);
      quantityRef.current.disabled = false;
    } else {
      quantityRef.current.disabled = true;
    }

    if (value > 0 && selectedProduct && selectedProduct.quantity > 0) {
      if (orderCodeValidation) {
        buttonRef.current.disabled = false;
      }
    }
  };

  const save_picking = async (event) => {
    event.currentTarget.disabled = true;

    const formData = new FormData();
    formData.append("order_id", selectedProduct.order_code);
    formData.append("product_id", selectedProduct.id);
    formData.append("product_code", selectedProduct.code);
    formData.append("upc", selectedProduct.upc || selectedProduct.code);
    formData.append("quantity", value);
    formData.append("total", selectedProduct.quantity);

    await axios
      .post(`${API_SERVER}picking/save_picking`, formData)
      .then(({ data }) => {
        console.log(data);
        if (data) {
          updatePickingTable();
          getOrderDetails();
          getAllPicking(); // Actualizar historial
          setCount(0);
          setValue(0);
          setOrderCodeValidation(0);
          setManualCode("");
          event.currentTarget.disabled = false;
        }
      });
  };

  const handleStartPicking = async (product) => {
    const formData = new FormData();
    formData.append("reference", reference);
    formData.append("sku", product.code);

    await axios
      .get(`${API_SERVER}picking/` + reference + `/` + product.code)
      .then(({ data }) => {
        console.log(data);
        setSelectedProduct(data.product_data);
        setOrder(data.order);
      });
  };

  const handleDeletePicking = async (pickingItem) => {
    if (deletingId === pickingItem.id) {
      return;
    }

    setDeletingId(pickingItem.id);

    try {
      console.log("Intentando eliminar picking:", pickingItem);

      // Intentar eliminar el picking via API correcta
      if (pickingItem.id) {
        await axios.post(`${API_SERVER}picking/unpick_product`, {
          picking_id: pickingItem.id,
        });
      }

      // Actualizar todas las tablas incluyendo historial
      await updatePickingTable();
      await getOrderDetails();
      await getAllPicking(); // Actualizar historial

      console.log("Picking eliminado exitosamente");
    } catch (error) {
      console.log("Error eliminando picking:", error);
      // Si falla la eliminacion, igual actualizamos las tablas incluyendo historial
      await updatePickingTable();
      await getOrderDetails();
      await getAllPicking(); // Actualizar historial
    } finally {
      setDeletingId(null);
    }
  };

  const getProductStatusBadge = (product) => {
    const pickingProduct = allPicking.find(
      (p) => p.product_code === product.code || p.code === product.code
    );
    const pickedQuantity = pickingProduct ? pickingProduct.quantity || 0 : 0;
    const remainingQuantity = (product.quantity || 0) - pickedQuantity;

    if (remainingQuantity <= 0) {
      return (
        <Badge
          bg="blue.500"
          color="white"
          fontSize="xs"
          p="6px 10px"
          borderRadius="md"
        >
          Pickeado
        </Badge>
      );
    } else if (pickedQuantity > 0) {
      return (
        <Badge
          bg="orange.400"
          color="white"
          fontSize="xs"
          p="6px 10px"
          borderRadius="md"
        >
          {pickedQuantity}/{product.quantity || 0}
        </Badge>
      );
    } else {
      return (
        <Badge
          bg="green.400"
          color="white"
          fontSize="xs"
          p="6px 10px"
          borderRadius="md"
        >
          {product.quantity || 0} unids.
        </Badge>
      );
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* Scanner USB input oculto */}
      <Input
        ref={quantityRef}
        value={manualCode}
        onChange={validate_code}
        onKeyDown={(e) => {
          // Capturar Enter del scanner
          if (e.key === "Enter" || e.keyCode === 13) {
            const cleanCode = manualCode.replace(/[\r\n]/g, "").trim();
            console.log("Enter detectado, código:", cleanCode);
            if (cleanCode && cleanCode.length > 0) {
              processCode(cleanCode, "scanner");
              setManualCode("");
            }
          }
        }}
        placeholder="Scanner USB input"
        position="absolute"
        left="-9999px"
        top="-9999px"
        opacity="0"
        width="1px"
        height="1px"
        padding="0"
        margin="0"
        overflow="hidden"
        border="none"
        autoFocus
        style={{ imeMode: "disabled" }} // Desactivar IME para evitar interferencias
      />

      <SimpleGrid columns={{ sm: 1, md: 2, xl: 2 }} spacing="24px">
        {/* Tabla de productos */}
        <Card bg={useColorModeValue("white", "gray.700")}>
          <CardHeader>
            <Text fontSize="lg" color={textColor} fontWeight="bold">
              📦 Lista de Productos
            </Text>
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color={textColor}>Nombre</Th>
                  <Th color={textColor}>SKU</Th>
                  <Th color={textColor}>Ubicación</Th>
                  <Th color={textColor}>Cantidad</Th>
                </Tr>
              </Thead>
              <Tbody>
                {products && products.length > 0 ? (
                  products.map((product, index) => (
                    <Tr key={product.code}>
                      <Td>
                        <Text
                          fontSize="md"
                          color={textColor}
                          fontWeight="medium"
                        >
                          {product.title || product.name || "Sin nombre"}
                        </Text>
                      </Td>
                      <Td>
                        <Text
                          fontSize="md"
                          color={textColor}
                          fontWeight="medium"
                        >
                          {product.code || "N/A"}
                        </Text>
                      </Td>
                      <Td>
                        <Text
                          fontSize="md"
                          color={textColor}
                          fontWeight="medium"
                        >
                          {product.location || product.ubicacion || "N/A"}
                        </Text>
                      </Td>
                      <Td>{getProductStatusBadge(product)}</Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={4} textAlign="center">
                      <Text color="gray.500">No se encontraron productos</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>

        {/* Tabla de historial de picking */}
        <Card bg={useColorModeValue("white", "gray.700")}>
          <CardHeader>
            <Text fontSize="lg" color={textColor} fontWeight="bold">
              📊 Historial de Picking
            </Text>
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color={textColor}>Producto</Th>
                  <Th color={textColor}>Cantidad</Th>
                  <Th color={textColor}>Fecha</Th>
                  <Th color={textColor}>Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {allPicking && allPicking.length > 0 ? (
                  allPicking.map((row, key) => {
                    const productCode = row.product_code || row.code;
                    const product = products.find((p) => p.code === productCode);
                    const productName = product ? (product.title || product.name) : "Sin nombre";

                    return (
                      <Tr key={key}>
                        <Td>
                          <Text fontSize="md" color={textColor} fontWeight="bold">
                            {productName}
                          </Text>
                        </Td>
                        <Td>
                          <Badge
                            bg={bgStatus}
                            color={colorStatus}
                            fontSize="xs"
                            p="6px 10px"
                            borderRadius="md"
                          >
                            {row.quantity}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="xs" color={textColor} fontWeight="500">
                            {row.updated_at || row.date_add || row.created_at}
                          </Text>
                        </Td>
                        <Td>
                          <Button
                            bg="red.500"
                            color="white"
                            size="sm"
                            onClick={() => handleDeletePicking(row)}
                            isLoading={deletingId === row.id}
                            loadingText="Eliminando..."
                          >
                            {deletingId === row.id ? "Eliminando..." : "Eliminar"}
                          </Button>
                        </Td>
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={4} textAlign="center">
                      <Text color="gray.500">No hay registros de picking</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </SimpleGrid>
    </Flex>
  );
}

export default OrderDetails;
